import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Link2 } from "lucide-react";

const CATEGORIES = [
  { value: "gaji", label: "Gaji Karyawan" },
  { value: "pajak", label: "Pajak" },
  { value: "operasional", label: "Operasional" },
  { value: "peralatan", label: "Peralatan" },
  { value: "lainnya", label: "Lainnya" },
];
const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const catLabel = (c) => CATEGORIES.find((x) => x.value === c)?.label || c;
const catColor = {
  gaji: "bg-violet-100 text-violet-800",
  pajak: "bg-red-100 text-red-800",
  operasional: "bg-blue-100 text-blue-800",
  peralatan: "bg-amber-100 text-amber-800",
  lainnya: "bg-slate-100 text-slate-700",
};

export default function Pengeluaran() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "operasional",
    description: "",
    amount: "",
  });

  const load = async () => setRows((await api.get(`/expenses?month=${month}`)).data);
  useEffect(() => { load(); }, [month]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Pengeluaran dicatat — otomatis mengurangi profit di menu Omset");
      setForm({ ...form, description: "", amount: "" });
      setShow(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Pengeluaran dihapus");
      setConfirmDelId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus"); }
  };

  const total = (rows || []).reduce((a, r) => a + r.amount, 0);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Keuangan</p>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Pengeluaran</h1>
          <p className="text-sm text-slate-600 mt-1">Catat gaji, pajak, operasional, dll. Permintaan belanja yang di-ACC otomatis masuk ke sini.</p>
        </div>
        <button data-testid="new-expense-btn" onClick={() => setShow(!show)} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] transition-transform flex-shrink-0">
          <Plus size={16} /> Catat
        </button>
      </div>

      <label className="block max-w-xs">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan</span>
        <input data-testid="expense-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
      </label>

      <div data-testid="expense-total" className="bg-slate-900 text-white rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 text-red-300 flex items-center justify-center"><Wallet size={20} /></div>
          <p className="text-sm text-slate-300">Total pengeluaran bulan ini</p>
        </div>
        <p className="text-2xl font-extrabold" style={{fontFamily:'Manrope'}}>{fmt(total)}</p>
      </div>

      {show && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Catat Pengeluaran</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Tanggal</span>
              <input data-testid="exp-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Kategori</span>
              <select data-testid="exp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Deskripsi</span>
            <input data-testid="exp-desc" type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="misal: Gaji Soleh bulan Agustus" className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Jumlah (Rp)</span>
            <input data-testid="exp-amount" type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required placeholder="1500000" className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
          </label>
          <button data-testid="exp-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Simpan Pengeluaran</button>
        </form>
      )}

      <div className="space-y-2">
        {rows && rows.length === 0 && <p className="text-sm text-slate-500">Belum ada pengeluaran bulan ini.</p>}
        {(rows || []).map((x) => (
          <div key={x.id} data-testid={`expense-${x.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${catColor[x.category] || catColor.lainnya}`}>{catLabel(x.category)}</span>
                {x.source === "purchase_request" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-100 text-cyan-800 text-[10px] font-bold uppercase tracking-wider"><Link2 size={10} /> Dari Permintaan</span>
                )}
              </div>
              <p className="mt-1 font-semibold text-slate-900 truncate">{x.description}</p>
              <p className="text-xs text-slate-500">{new Date(x.date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })} · oleh {x.created_by_name}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <p className="font-extrabold text-red-600" style={{fontFamily:'IBM Plex Sans'}}>-{fmt(x.amount)}</p>
              {confirmDelId === x.id ? (
                <div className="flex gap-2">
                  <button data-testid={`confirm-del-exp-${x.id}`} onClick={() => del(x.id)} className="h-9 px-3 rounded-lg bg-red-600 text-white text-xs font-bold">Ya, Hapus</button>
                  <button data-testid={`cancel-del-exp-${x.id}`} onClick={() => setConfirmDelId(null)} className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold">Batal</button>
                </div>
              ) : (
                <button data-testid={`del-exp-${x.id}`} onClick={() => setConfirmDelId(x.id)} className="w-9 h-9 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={15} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
